import {
  AllowMeErrorOutcomes,
  AllowMeErrorPhases,
  AllowMeErrorReasons,
  AllowMeProcessStepLabels,
  AllowMeProcessSteps,
} from './allowme-error-reasons';

export const ALLOWME_ERROR_SCHEMA_VERSION = 1;
const MAX_STACK_LENGTH = 8000;

const serializeError = (error) => {
  if (!error) {
    return {};
  }

  let stack = error.stack ? String(error.stack) : undefined;
  if (stack && stack.length > MAX_STACK_LENGTH) {
    stack = stack.slice(0, MAX_STACK_LENGTH);
  }

  return {
    errorType: error.name || (error.constructor && error.constructor.name) || 'Error',
    errorCode: error.code,
    errorMessage: error.message || String(error),
    errorDetails: error.details != null ? String(error.details) : undefined,
    stack,
  };
};

export const buildAllowMeErrorEntry = ({
  reason,
  phase,
  outcome,
  causeReason,
  message,
  loginFlow,
  document,
  error,
  allowMeFlag,
  allowMeCanCollect,
  allowMeInitPluginInvoked,
  allowMeLastInitDiagnostic,
  allowMeApiKeyPreview,
  device,
  processStep,
  failedOperation,
  sdkUrl,
  ambiente,
  timeoutMs,
  collectEvent,
  hint,
  originProcessStep,
  likelyCause,
  likelyCauseLabel,
  correctionHint,
  apiKeyLength,
  sdkConsole,
  sdkNetwork,
  missingEndpoints,
  alwmMethods,
  elapsedMs,
  initDurationMs,
  initResultType,
} = {}) => {
  const serialized = serializeError(error);
  const documentValue = document || null;

  return {
    reason,
    phase,
    outcome,
    causeReason,
    message: message || serialized.errorMessage,
    loginFlow,
    document: documentValue,
    documentAvailable: Boolean(documentValue),
    errorType: serialized.errorType,
    errorCode: serialized.errorCode || (error && error.code),
    errorMessage: serialized.errorMessage,
    errorDetails: serialized.errorDetails,
    stack: serialized.stack,
    allowMeFlag,
    allowMeCanCollect,
    allowMeInitPluginInvoked,
    allowMeLastInitDiagnostic,
    allowMeApiKeyPreview,
    device,
    processStep,
    processStepLabel: processStep ? AllowMeProcessStepLabels[processStep] : undefined,
    failedOperation,
    sdkUrl,
    ambiente,
    timeoutMs,
    collectEvent,
    hint,
    originProcessStep,
    originProcessStepLabel: originProcessStep
      ? AllowMeProcessStepLabels[originProcessStep]
      : undefined,
    likelyCause,
    likelyCauseLabel,
    correctionHint,
    apiKeyLength,
    sdkConsole,
    sdkNetwork,
    missingEndpoints,
    alwmMethods,
    elapsedMs,
    initDurationMs,
    initResultType,
  };
};

export const allowMeErrorEntryToJson = (
  entry,
  { brand, env, timestampMs, serverTimestamp, deviceOverride } = {}
) => {
  const payload = {
    schema_version: ALLOWME_ERROR_SCHEMA_VERSION,
    timestamp_ms: timestampMs,
    server_timestamp: serverTimestamp,
    brand,
    env,
    channel: 'ib_web',
    document: entry.document || null,
    document_available: Boolean(entry.documentAvailable),
    reason: entry.reason,
    phase: entry.phase,
    outcome: entry.outcome,
    device: deviceOverride || entry.device || {},
  };

  const optional = {
    login_flow: entry.loginFlow,
    cause_reason: entry.causeReason,
    message: entry.message,
    error_type: entry.errorType,
    error_code: entry.errorCode,
    error_message: entry.errorMessage,
    error_details: entry.errorDetails,
    stack: entry.stack,
    allowme_flag: entry.allowMeFlag,
    allowme_can_collect: entry.allowMeCanCollect,
    allowme_init_plugin_invoked: entry.allowMeInitPluginInvoked,
    allowme_last_init_diagnostic: entry.allowMeLastInitDiagnostic,
    allowme_api_key_preview: entry.allowMeApiKeyPreview,
    process_step: entry.processStep,
    process_step_label: entry.processStepLabel,
    failed_operation: entry.failedOperation,
    sdk_url: entry.sdkUrl,
    ambiente: entry.ambiente,
    timeout_ms: entry.timeoutMs,
    collect_event: entry.collectEvent,
    hint: entry.hint,
    origin_process_step: entry.originProcessStep,
    origin_process_step_label: entry.originProcessStepLabel,
    likely_cause: entry.likelyCause,
    likely_cause_label: entry.likelyCauseLabel,
    correction_hint: entry.correctionHint,
    api_key_length: entry.apiKeyLength,
    sdk_console: entry.sdkConsole,
    sdk_network: entry.sdkNetwork,
    missing_endpoints: entry.missingEndpoints,
    alwm_methods: entry.alwmMethods,
    elapsed_ms: entry.elapsedMs,
    init_duration_ms: entry.initDurationMs,
    init_result_type: entry.initResultType,
  };

  Object.keys(optional).forEach((key) => {
    if (optional[key] !== undefined && optional[key] !== null) {
      payload[key] = optional[key];
    }
  });

  return payload;
};

export const allowMeInitMissingKeyEntry = ({
  apiKeyPreview,
  sdkUrl,
  ambiente,
} = {}) =>
  buildAllowMeErrorEntry({
    reason: AllowMeErrorReasons.initMissingKey,
    phase: AllowMeErrorPhases.bootstrapInit,
    outcome: AllowMeErrorOutcomes.initDisabledCollection,
    message: 'ALLOWME_KEY empty',
    allowMeCanCollect: false,
    allowMeInitPluginInvoked: false,
    allowMeApiKeyPreview: apiKeyPreview,
    processStep: AllowMeProcessSteps.sdkInit,
    failedOperation: 'Alwm.init',
    sdkUrl,
    ambiente,
  });

export const allowMeInitFailedEntry = ({
  message,
  diagnostic,
  initPluginWasInvoked = true,
  apiKeyPreview,
  sdkUrl,
  ambiente,
} = {}) =>
  buildAllowMeErrorEntry({
    reason: AllowMeErrorReasons.initFailed,
    phase: AllowMeErrorPhases.bootstrapInit,
    outcome: AllowMeErrorOutcomes.initDisabledCollection,
    message,
    allowMeCanCollect: false,
    allowMeInitPluginInvoked: initPluginWasInvoked,
    allowMeLastInitDiagnostic: diagnostic || message,
    allowMeApiKeyPreview: apiKeyPreview,
    processStep: AllowMeProcessSteps.sdkInit,
    failedOperation: 'Alwm.init',
    sdkUrl,
    ambiente,
  });

export const allowMeInitExceptionEntry = ({
  error,
  apiKeyPreview,
  processStep = AllowMeProcessSteps.sdkInit,
  failedOperation = 'Alwm.init',
  sdkUrl,
  ambiente,
  timeoutMs,
  hint,
} = {}) =>
  buildAllowMeErrorEntry({
    reason: AllowMeErrorReasons.initException,
    phase: AllowMeErrorPhases.bootstrapInit,
    outcome: AllowMeErrorOutcomes.initDisabledCollection,
    error,
    allowMeCanCollect: false,
    allowMeInitPluginInvoked: true,
    allowMeApiKeyPreview: apiKeyPreview,
    processStep,
    failedOperation,
    sdkUrl,
    ambiente,
    timeoutMs,
    hint,
  });
